import type { FundraisingResponse } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import {
  type FundraisingDraft,
  toCreateRequest,
} from '../../_shared/domain/fundraising-draft.model';
import { validateDraft } from '../../_shared/domain/fundraising-draft.validation';
import { toFundraisingFormError } from '../../_shared/domain/fundraising-form-error';
import type { AddFundraisingGateway } from '../infrastructure/add-fundraising.gateway';
import { submitRequested } from './add-fundraising.actions';
import { selectDraft } from './add-fundraising.selectors';
import { addFundraisingActions } from './add-fundraising.slice';

/**
 * WORKER: create the fundraising. Validation runs first (pure domain); on
 * failure it dispatches a `validation` business error and stops WITHOUT
 * touching the gateway. Otherwise all I/O goes through the injected gateway.
 * `takeLatest` means a re-submit supersedes an in-flight one.
 */
function* saveWorker() {
  const draft = (yield select(selectDraft)) as FundraisingDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(addFundraisingActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: AddFundraisingGateway = yield getContext('addFundraisingGateway');
  yield put(addFundraisingActions.saveStarted());
  try {
    const created = (yield call(
      gateway.createFundraising,
      toCreateRequest(draft),
    )) as FundraisingResponse;
    yield put(addFundraisingActions.saveSucceeded(created));
  } catch (e) {
    yield put(
      addFundraisingActions.saveFailed(
        toFundraisingFormError(e, 'Could not create the round. Try again.'),
      ),
    );
  }
}

/** WATCHERS. */
export function* addFundraisingSaga() {
  yield all([takeLatest(submitRequested.type, saveWorker)]);
}
